<script lang="ts">
  import type { Product } from "$lib/types/products";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import { page } from "$app/state";

  interface Props {
    product: Product;
  }

  let { product }: Props = $props();

  let anchor = $derived(page.url.hash.replace("#", ""));
  let selectedTab = $derived(anchor || "funksjoner");
</script>

<ul
  role="tablist"
  aria-label="Produktinformasjon"
  class="list-none flex gap-3 tablet:gap-8 px-0 py-2 mt-8 mb-4 mx-edge-x border-b-solid border-1 pb-0 border-$primary-light overflow-x-auto overflow-y-hidden tablet:overflow-initial max-w-prose"
>
  <li role="presentation">
    <button
      id="funksjoner_tab"
      class:selected-tab={selectedTab === "funksjoner"}
      class="tab-button hydivhens-manual whitespace-nowrap"
      onclick={() => {
        selectedTab = "funksjoner";
        location.hash = "funksjoner";
      }}
      role="tab"
      aria-selected={selectedTab === "funksjoner"}
      aria-current={selectedTab === "funksjoner" ? "page" : undefined}
      aria-controls="funksjoner"
    >
      Funksjoner
    </button>
  </li>
  <li role="presentation">
    <button
      id="detaljer_tab"
      class:selected-tab={selectedTab === "detaljer"}
      class="tab-button hyphens-manual whitespace-nowrap"
      onclick={() => {
        selectedTab = "detaljer";
        location.hash = "detaljer";
      }}
      role="tab"
      aria-selected={selectedTab === "detaljer"}
      aria-current={selectedTab === "detaljer" ? "page" : undefined}
      aria-controls="detaljer">Produktdetaljer</button
    >
  </li>
  <li role="presentation">
    <button
      id="produktdatablad_tab"
      class:selected-tab={selectedTab === "produktdatablad"}
      class="tab-button hyphens-manual whitespace-nowrap"
      onclick={() => {
        selectedTab = "produktdatablad";
        location.hash = "produktdatablad";
      }}
      role="tab"
      aria-selected={selectedTab === "produktdatablad"}
      aria-current={selectedTab === "produktdatablad" ? "page" : undefined}
      aria-controls="produktdatablad">Produktdatablad</button
    >
  </li>
  {#if product.heat_efficiency}
    <li role="presentation">
      <button
        id="varmeeffekt_tab"
        class:selected-tab={selectedTab === "varmeeffekt"}
        class="tab-button hyphens-manual whitespace-nowrap"
        onclick={() => {
          selectedTab = "varmeeffekt";
          location.hash = "varmeeffekt";
        }}
        role="tab"
        aria-selected={selectedTab === "varmeeffekt"}
        aria-current={selectedTab === "varmeeffekt" ? "page" : undefined}
        aria-controls="varmeeffekt">Varmeeffekt</button
      >
    </li>
  {/if}
</ul>

<div
  role="tabpanel"
  class="mx-$space-edge-x mb-8"
  id="funksjoner"
  aria-labelledby="funksjoner_tab"
  hidden={selectedTab !== "funksjoner"}
>
  <PortableText
    value={product.functions}
    components={contentBlockComponents}
    onMissingComponent={false}
  />
</div>
<div
  role="tabpanel"
  class="mx-$space-edge-x mb-8"
  id="detaljer"
  aria-labelledby="detaljer_tab"
  hidden={selectedTab !== "detaljer"}
>
  {#if product.specifications?.rows?.length > 0}
    <table class="border-none w-full">
      <tbody>
        <!-- eslint-disable-next-line svelte/require-each-key -->
        {#each product.specifications?.rows as row}
          <tr class="border-none">
            <!-- eslint-disable-next-line svelte/require-each-key -->
            {#each row.cells as cell, cellIndex}
              {#if cellIndex === 0}
                <th scope="row">{cell}</th>
              {:else}
                <td class="text-right">{cell}</td>
              {/if}
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>
<div
  role="tabpanel"
  class="mx-$space-edge-x mb-8"
  id="produktdatablad"
  aria-labelledby="produktdatablad"
  hidden={selectedTab !== "produktdatablad"}
>
  <p>Produktdatablad og andre dokumenter</p>
  {#if product.documents?.length > 0}
    <ul>
      {#each product.documents as doc (doc)}
        <li>
          <!-- To download file on open: href={`${doc.url}?dl=`}  -->
          <a href={`${doc.url}`} target="_blank">{doc.title ?? "Fil uten navn"}</a>
        </li>
      {/each}
    </ul>
  {/if}
</div>
{#if product.heat_efficiency}
  <div>
    <div
      role="tabpanel"
      class="mx-$space-edge-x mb-8"
      id="varmeeffekt"
      aria-labelledby="varmeeffekt_tab"
      hidden={selectedTab !== "varmeeffekt"}
    >
      <PortableText
        value={product.heat_efficiency}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>
  </div>
{/if}

<style lang="postcss">
  .tab-button {
    background: none;
    border: 3px solid transparent;
    padding: 0;
    font-family: var(--font-body);
  }
  .tab-button:focus {
    border: 3px solid var(--focus);
    outline: none;
  }
  .selected-tab {
    border-bottom: 3px solid var(--primary-strong);
    font-weight: 700;
  }
</style>
